// Créer l'espace d'un club, sans compte (D-088) : /#/creer/<jeton>, le lien reçu par e-mail après une
// demande d'inscription sur la page de vente. Vouvoiement : le club n'est pas encore client.
// La page relit la demande, cherche le club et ses équipes engagées à la FFBB DEPUIS L'APPAREIL (comme
// la console : la FFBB refuse les serveurs), propose les équipes (on décoche), le nom, le sigle et la
// couleur, puis crée l'espace et enchaîne sur le lien de la présidence (/i/<jeton>) : le compte s'ouvre
// sans nouvel e-mail. FFBB introuvable ou muette : l'espace se crée quand même, sans les équipes.
import { signupCreateClub, signupView, type SignupView } from '../lib/api';
import { clubEngagements, clubOrganisme } from '../lib/ffbb';
import { BRAND_PRESETS, monogram } from '../domain/brand';
import { groupEngagements, seasonFor, sigle, titleCase, type TeamDraft } from '../domain/console';
import { createPayload, deClub, draftProblem } from '../domain/signup';
import { $, $$, html, mount, type Html } from './dom';
import { applyBrand } from './theme';
import { renderValidate } from './gratuite-pub';

const SITE = 'https://arceau.app/';
const DEFAULT = '#E8590C';   // orange Arceau, tant que le club n'a pas choisi
type Ffbb = 'none' | 'searching' | 'found' | 'unknown' | 'down';
interface Draft {
  name: string; short: string; color: string; touched: boolean; teams: TeamDraft[];
  ffbb: Ffbb; org: { name: string; town: string | null; committee: string | null } | null; error: string; busy: boolean;
}

const until = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const ball = html`<span class="ball" aria-hidden="true"></span>`;
/** Le ballon Arceau dans un rond blanc : lisible sur le bandeau à la couleur du club. */
const badge = html`<span class="pub-logo sc-badge" aria-hidden="true">${ball}</span>`;

/** Lien faux, expiré, déjà servi (ou pas de réseau) : ce qu'on peut faire. */
function missing(root: HTMLElement, offline: boolean): void {
  applyBrand(null, false);
  document.title = 'Lien expiré · Arceau';
  mount(root, html`<main class="pub-missing">${ball}
    <h1>${offline ? 'Pas de connexion' : 'Lien expiré ou déjà utilisé'}</h1>
    <p>${offline ? 'Vérifiez votre connexion, puis rouvrez le lien reçu par e-mail.' : 'Ce lien sert une fois et reste valable 14 jours.'}</p>
    ${offline ? '' : html`<p>L’espace du club est déjà créé ? Connectez-vous avec l’adresse de votre demande. Sinon, refaites une demande sur arceau.app.</p>`}
    <div class="btn-row"><a class="btn btn-primary" href="/">Se connecter</a><a class="btn btn-ghost" href="${SITE}#inscription">arceau.app</a></div>
  </main>`);
}

/** Le club est déjà sur Arceau (code FFBB ou nom) : on ne crée pas de doublon. */
function taken(root: HTMLElement, club: string): void {
  applyBrand(null, false);
  document.title = 'Déjà sur Arceau';
  mount(root, html`<div class="pub sc-pub">
    <header class="pub-head"><div class="pub-id">${badge}<div><h1>${club}</h1><p>Créer l’espace du club · Arceau</p></div></div></header>
    <main class="pub-main"><section class="pub-thanks sc-taken"><p class="pub-kicker">Déjà sur Arceau</p>
      <h3>Ce club a déjà son espace</h3>
      <p>Ce club est déjà sur Arceau : demandez une invitation à son bureau.</p>
      <p class="muted">Le bureau vous envoie un lien depuis l’app ; vous entrez avec votre adresse e-mail, sans mot de passe.</p>
      <div class="btn-row"><a class="btn btn-ghost" href="/">J’ai déjà un compte</a></div></section></main>
    ${foot(null)}
  </div>`);
}

const foot = (expires: string | null) => html`<footer class="pub-foot">${ball}<div><strong>Arceau</strong>
  <small>L’app des clubs de basket${expires ? ` · lien personnel, valable jusqu’au ${until(expires)}` : ''}</small></div></footer>`;

export async function renderSignupCreate(root: HTMLElement, token: string): Promise<void> {
  let v: SignupView | null = null, offline = false;
  try { v = await signupView(token); } catch { offline = true; }
  // L'équipe gratuite (D-166) : le lien de validation envoyé à l'adresse officielle d'un club en mode gratuit.
  if (!v && !offline && await renderValidate(root, token)) return;
  if (!v) { missing(root, offline); return; }
  if (v.taken) { taken(root, v.club_name); return; }
  const view = v;
  const season = seasonFor(new Date()).name;
  const d: Draft = {
    name: view.club_name, short: sigle(view.club_name), color: DEFAULT, touched: false, teams: [],
    ffbb: view.ffbb_code ? 'searching' : 'none', org: null, error: '', busy: false,
  };
  const brand = () => applyBrand({ id: `creer:${token.slice(0, 8)}`, name: d.name.trim() || view.club_name, short_name: d.short.trim() || null, logo_url: null, primary_color: d.color }, false);

  function ffbbCard(): Html | string {
    const code = view.ffbb_code ?? '';
    if (d.ffbb === 'searching') return html`<div class="card sc-ffbb"><p class="muted">Recherche du club ${code} à la FFBB…</p></div>`;
    if (d.ffbb === 'found' && d.org) {
      const n = d.teams.reduce((k, t) => k + t.links.length, 0);
      return html`<div class="card cn-found"><p class="eyebrow cn-ok">✓ Trouvé sur la FFBB · ${code}</p><span class="cn-found-name">${titleCase(d.org.name)}</span>
        <span class="muted small">${[d.org.town ? titleCase(d.org.town) : '', d.org.committee ? titleCase(d.org.committee) : '',
          n ? `${n} ${n > 1 ? 'engagements' : 'engagement'} en ${season}` : `aucun engagement en ${season} pour l’instant`].filter(Boolean).join(' · ')}</span></div>`;
    }
    if (d.ffbb === 'unknown') return html`<div class="card sc-warn"><p class="eyebrow">Code FFBB introuvable · ${code}</p>
      <p>Nous ne trouvons pas ce code à la FFBB. Vous pouvez créer l’espace maintenant avec le nom du club : les équipes s’ajouteront ensuite.</p></div>`;
    if (d.ffbb === 'down') return html`<div class="card sc-warn"><p class="eyebrow">La FFBB ne répond pas</p>
      <p>Impossible de lire vos équipes engagées pour l’instant.</p>
      <div class="btn-row"><button type="button" class="btn btn-ghost btn-sm" id="sc-retry">Réessayer</button></div>
      <p class="muted small">Vous pouvez aussi créer l’espace sans les équipes : elles s’ajouteront ensuite.</p></div>`;
    return '';
  }

  function draw(): void {
    brand();
    mount(root, html`<div class="pub sc-pub">
      <header class="pub-head"><div class="pub-id"><span class="pub-logo pub-mono" id="sc-mono" aria-hidden="true">${monogram(d.name || view.club_name, d.short)}</span>
        <div><h1 id="sc-h1">${d.name.trim() || view.club_name}</h1><p>Créer l’espace du club · essai gratuit</p></div></div></header>
      <main class="pub-main">
        <p class="lead">Bonjour ${view.contact_name}. En 2 minutes : vérifiez le club et ses équipes, choisissez sa couleur, puis entrez dans l’espace.</p>
        ${ffbbCard()}
        <form class="form sc-form" id="sc-form" novalidate>
          <section class="pub-sec"><h2>Le club</h2>
            <div class="row-2 sc-names"><label class="field"><span>Nom du club</span><input id="sc-name" value="${d.name}" maxlength="120" autocomplete="organization"></label>
              <label class="field"><span>Sigle</span><input id="sc-short" value="${d.short}" maxlength="6" autocapitalize="characters" placeholder="ex. BCR"></label></div>
            <div class="field"><span>Couleur du club <span class="muted small">· barre, bandeaux, boutons</span></span>
              <div class="swatches">
                ${BRAND_PRESETS.map(c => html`<button type="button" class="swatch" data-color="${c}" style="--sw:${c}" aria-label="Couleur ${c}" aria-pressed="${String(c === d.color)}"></button>`)}
                <label class="swatch swatch-custom" title="Autre couleur"><input type="color" id="sc-color" value="${d.color.toLowerCase()}" aria-label="Autre couleur"></label>
              </div></div>
          </section>
          ${d.teams.length ? html`<section class="pub-sec"><h2>Les équipes</h2>
            <p class="muted small">Engagées en ${season}, déjà reliées à leurs calendriers FFBB. Décochez celles que vous ne voulez pas.</p>
            <div class="card sc-teams"><ul class="iv-list">${d.teams.map((t, i) => html`<li class="iv-role cn-team">
              <span class="iv-main"><strong>${t.name}</strong>
                <span class="muted small">${[...new Set(t.links.map(l => l.squad).filter(Boolean))].join(' · ') || `${t.links.length} ${t.links.length > 1 ? 'engagements' : 'engagement'}`} — ${t.links.map(l => l.competition_name).slice(0, 2).join(' · ')}${t.links.length > 2 ? '…' : ''}</span></span>
              <button type="button" class="iv-switch" role="switch" data-i="${i}" aria-checked="${String(t.on)}" aria-label="Créer ${t.name}"></button></li>`)}</ul></div>
          </section>` : ''}
          <section class="pub-sec"><h2>Votre accès</h2>
            <div class="card sc-me"><strong>${view.email}</strong>
              <span class="muted small">Avec les droits du bureau. Pas de mot de passe : vous entrez dans l’espace juste après sa création.</span></div>
          </section>
          <p class="form-msg" role="alert">${d.error}</p>
          <button type="submit" class="btn btn-primary" ${d.busy ? 'disabled' : ''}>${d.busy ? 'Création de l’espace…' : 'Créer l’espace du club'}</button>
          <p class="hint">Essai gratuit, sans engagement. Logo, coachs et membres : ensuite, depuis l’espace.</p>
        </form>
      </main>
      ${foot(view.expires_at)}
    </div>`);
    wire();
  }

  // Le nom et le sigle saisis, gardés avant chaque nouveau dessin.
  const keep = () => {
    const name = $<HTMLInputElement>(root, '#sc-name'), short = $<HTMLInputElement>(root, '#sc-short');
    if (name) d.name = name.value;
    if (short) d.short = short.value;
  };
  const head = () => {
    $(root, '#sc-h1')!.textContent = d.name.trim() || view.club_name;
    $(root, '#sc-mono')!.textContent = monogram(d.name.trim() || view.club_name, d.short);
    brand();
  };
  const pick = (c: string) => {
    d.color = c.toUpperCase();
    $$(root, '.swatch[data-color]').forEach(s => s.setAttribute('aria-pressed', String(s.dataset.color === d.color)));
    brand();
  };

  function wire(): void {
    $(root, '#sc-name')!.addEventListener('input', () => { keep(); d.touched = true; head(); });
    $(root, '#sc-short')!.addEventListener('input', () => { keep(); head(); });
    $$<HTMLButtonElement>(root, '.swatch[data-color]').forEach(s => s.addEventListener('click', () => pick(s.dataset.color!)));
    $<HTMLInputElement>(root, '#sc-color')!.addEventListener('input', e => pick((e.target as HTMLInputElement).value));
    $$<HTMLButtonElement>(root, '.iv-switch[data-i]').forEach(b => b.addEventListener('click', () => {
      const t = d.teams[Number(b.dataset.i)]!;
      t.on = !t.on;
      b.setAttribute('aria-checked', String(t.on));
    }));
    $(root, '#sc-retry')?.addEventListener('click', () => { keep(); void lookup(); });
    $(root, '#sc-form')!.addEventListener('submit', e => { e.preventDefault(); keep(); void create(); });
  }

  async function lookup(): Promise<void> {
    const code = view.ffbb_code;
    if (!code) return;
    d.ffbb = 'searching'; draw();
    let res: Pick<Draft, 'ffbb' | 'org' | 'teams'>;
    try {
      const [org, engs] = await Promise.all([clubOrganisme(code), clubEngagements(code)]);
      res = org ? { ffbb: 'found', org, teams: groupEngagements(engs) } : { ffbb: 'unknown', org: null, teams: [] };
    } catch { res = { ffbb: 'down', org: null, teams: [] }; }
    keep();
    Object.assign(d, res);
    // Le nom officiel (la FFBB écrit en capitales) et son sigle, sauf si la personne a déjà corrigé.
    if (res.org && !d.touched) { d.name = titleCase(res.org.name); d.short = sigle(d.name); }
    draw();
  }

  async function create(): Promise<void> {
    if (d.busy) return;
    const problem = draftProblem(d);
    if (problem) { d.error = problem; draw(); return; }
    d.busy = true; d.error = ''; draw();
    try {
      const r = await signupCreateClub(token, createPayload({ name: d.name, short: d.short, color: d.color, ffbb: d.ffbb === 'found', teams: d.teams }));
      // Le lien de la présidence, réservé à l'adresse de la demande : le compte s'ouvre sans nouvel e-mail.
      location.replace(`/i/${r.invite.token}`);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      if (err.code === '23505') { taken(root, d.name.trim() || view.club_name); return; }
      if (err.code === 'P0002') { missing(root, false); return; }
      d.busy = false;
      d.error = err.code === '22023' ? 'Vérifiez le nom, le sigle et les équipes.' : 'Création impossible pour l’instant. Réessayez dans un instant.';
      draw();
    }
  }

  draw();
  void lookup();
}
